import { parseSitelinkItems } from './richResultSchema.js';

/**
 * Hyper-local SEO campaign settings for public marketing pages.
 *
 * Tools & Bubbles ships Kochi defaults (HQ map, competitor hooks, GBP playbook).
 * SaaS tenants start empty and configure their own market in Super Admin.
 */

export type LocalSeoCompetitorRow = {
  name: string;
  positioning: string;
  advantage: string;
};

export type LocalSeoCampaignConfig = {
  /** Embed the organisation HQ map on neighbourhood landing pages. */
  showHqMapOnLocationPages: boolean;
  /** Show the competitor / feature matrix on location pages. */
  showCompetitorMatrix: boolean;
  competitorMatrixTitle: string;
  competitorMatrixIntro: string;
  competitorRows: LocalSeoCompetitorRow[];
  /** Google review short-link used in thank-you / review-request copy. */
  googleReviewUrl: string;
  /** Minutes after job completion before a review request is due (playbook + future automation). */
  reviewRequestDelayMinutes: number;
  reviewRequestWhatsAppEnabled: boolean;
  gbpPrimaryCategory: string;
  gbpSecondaryCategories: string[];
  gbpServiceAreas: string[];
  gbpAttributes: string[];
  /** City/region neighbourhoods are contained in (empty = no parent market on SaaS tenants). */
  parentMarketName: string;
  /** Emit Offer + starting price on service landing pages when a from-price exists. */
  emitServiceOffers: boolean;
  /** UN/CEFACT-style unit label for Offer (e.g. service, seat). */
  offerUnitText: string;
  /** Emit BlogPosting JSON-LD on article pages. */
  emitBlogPosting: boolean;
  /** Author/publisher label on BlogPosting (empty uses the organisation name). */
  blogAuthorName: string;
  /** Emit ItemList sitelink candidates on public pages. */
  emitSitelinkNavigation: boolean;
  sitelinkItems: Array<{ name: string; path: string }>;
  /** Emit WebSite SearchAction targeting /search. */
  emitWebsiteSearch: boolean;
  /** Attach ImageObject on service and location graphs. */
  emitImageObjects: boolean;
  /** YouTube/Vimeo URL — VideoObject is omitted when empty or not a real video host. */
  videoEmbedUrl: string;
  videoName: string;
  videoThumbnailUrl: string;
  videoUploadDate: string;
};

export const TB_DEFAULT_COMPETITOR_ROWS: LocalSeoCompetitorRow[] = [
  {
    name: 'Handy sQuad',
    positioning: 'High-end boutique deep cleans',
    advantage:
      'Instant web booking plus the Bubbles AI scheduling assistant — book a Kakkanad or Edappally slot in under a minute, no phone tag.',
  },
  {
    name: 'My Homey',
    positioning: 'Bulk residential, pay-after-cleaning',
    advantage:
      'Fully insured (Workmen’s Compensation and Public Liability) with premium Kärcher equipment and 3M chemicals on every villa and apartment job.',
  },
  {
    name: 'Urban Company / Joboy',
    positioning: 'Aggregator app with gig workers',
    advantage:
      '100% in-house, background-verified professionals and 15+ years of Kochi operations since 2009 — not a marketplace of independent contractors.',
  },
];

export const TB_DEFAULT_GBP_SERVICE_AREAS = [
  'Kakkanad',
  'Edappally',
  'Palarivattom',
  'Vyttila',
  'Kaloor',
  'Panampilly Nagar',
  'Kadavanthara',
  'Infopark',
  'SmartCity Kochi',
  'Thrikkakara',
  'Aluva',
  'Kalamassery',
  'Maradu',
  'Thripunithura',
  'Angamaly',
  'Kalady',
  'Cherthala',
  'Alappuzha',
  'Thrissur',
  'Nedumbassery',
  'Chalakudy',
  'Malayattoor',
  'Fort Kochi',
  'Vypin',
];

export const TB_DEFAULT_GBP_ATTRIBUTES = ['Onsite services', 'Online appointments'];

export const TB_DEFAULT_SITELINK_ITEMS = [
  { name: 'Book now', path: '/book' },
  { name: 'Deep cleaning', path: '/deep-cleaning-kochi' },
  { name: 'Home cleaning', path: '/home-cleaning-kochi' },
  { name: 'Sofa cleaning', path: '/sofa-cleaning-kochi' },
  { name: 'Office cleaning', path: '/office-cleaning-kochi' },
  { name: 'Contact', path: '/contact-us' },
];

export function getCodeDefaultLocalSeoCampaign(isSaas = false): LocalSeoCampaignConfig {
  if (isSaas) {
    return {
      showHqMapOnLocationPages: true,
      showCompetitorMatrix: false,
      competitorMatrixTitle: 'Why customers choose us',
      competitorMatrixIntro: '',
      competitorRows: [],
      googleReviewUrl: '',
      reviewRequestDelayMinutes: 60,
      reviewRequestWhatsAppEnabled: false,
      gbpPrimaryCategory: 'Cleaning Service',
      gbpSecondaryCategories: [
        'House Cleaning Service',
        'Commercial Cleaning Service',
        'Upholstery Cleaning Service',
      ],
      gbpServiceAreas: [],
      gbpAttributes: [...TB_DEFAULT_GBP_ATTRIBUTES],
      parentMarketName: '',
      emitServiceOffers: true,
      offerUnitText: 'service',
      emitBlogPosting: true,
      blogAuthorName: '',
      emitSitelinkNavigation: false,
      sitelinkItems: [],
      emitWebsiteSearch: false,
      emitImageObjects: true,
      videoEmbedUrl: '',
      videoName: '',
      videoThumbnailUrl: '',
      videoUploadDate: '',
    };
  }
  return {
    showHqMapOnLocationPages: true,
    showCompetitorMatrix: true,
    competitorMatrixTitle: 'Why Kochi homes pick Tools & Bubbles',
    competitorMatrixIntro:
      'Handy sQuad, My Homey and the aggregator apps all operate in Kochi. Here is how an in-house, insured crew with instant booking wins the local search — and the job.',
    competitorRows: TB_DEFAULT_COMPETITOR_ROWS.map((row) => ({ ...row })),
    googleReviewUrl: 'https://g.page/r/CdUHjyNj-zPmEAE/review',
    reviewRequestDelayMinutes: 60,
    reviewRequestWhatsAppEnabled: true,
    gbpPrimaryCategory: 'Cleaning Service',
    gbpSecondaryCategories: [
      'House Cleaning Service',
      'Commercial Cleaning Service',
      'Upholstery Cleaning Service',
    ],
    gbpServiceAreas: [...TB_DEFAULT_GBP_SERVICE_AREAS],
    gbpAttributes: [...TB_DEFAULT_GBP_ATTRIBUTES],
    parentMarketName: 'Kochi',
    emitServiceOffers: true,
    offerUnitText: 'service',
    emitBlogPosting: true,
    blogAuthorName: 'Tools & Bubbles Team',
    emitSitelinkNavigation: true,
    sitelinkItems: TB_DEFAULT_SITELINK_ITEMS.map((item) => ({ ...item })),
    emitWebsiteSearch: true,
    emitImageObjects: true,
    videoEmbedUrl: '',
    videoName: '',
    videoThumbnailUrl: '',
    videoUploadDate: '',
  };
}

function parseCompetitorRows(raw: unknown): LocalSeoCompetitorRow[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const rows = raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const name = String(row.name ?? '').trim();
      const positioning = String(row.positioning ?? row.strategy ?? '').trim();
      const advantage = String(row.advantage ?? row.hook ?? '').trim();
      if (!name || !advantage) return null;
      return { name, positioning, advantage };
    })
    .filter((row): row is LocalSeoCompetitorRow => row !== null);
  return rows.length > 0 ? rows : undefined;
}

function parseStringList(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const items = raw.map((item) => String(item ?? '').trim()).filter(Boolean);
  return items.length > 0 ? items : undefined;
}

export function parseLocalSeoCampaign(raw: unknown): Partial<LocalSeoCampaignConfig> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const competitorRows = parseCompetitorRows(o.competitor_rows ?? o.competitorRows);
  const gbpSecondaryCategories = parseStringList(o.gbp_secondary_categories ?? o.gbpSecondaryCategories);
  const gbpServiceAreas = parseStringList(o.gbp_service_areas ?? o.gbpServiceAreas);
  const gbpAttributes = parseStringList(o.gbp_attributes ?? o.gbpAttributes);
  const delayRaw = Number(o.review_request_delay_minutes ?? o.reviewRequestDelayMinutes);
  const parsed: Partial<LocalSeoCampaignConfig> = {};

  if (o.show_hq_map_on_location_pages !== undefined || o.showHqMapOnLocationPages !== undefined) {
    const v = o.show_hq_map_on_location_pages ?? o.showHqMapOnLocationPages;
    parsed.showHqMapOnLocationPages = v === true || v === 'true' || v === 1 || v === '1';
  }
  if (o.show_competitor_matrix !== undefined || o.showCompetitorMatrix !== undefined) {
    const v = o.show_competitor_matrix ?? o.showCompetitorMatrix;
    parsed.showCompetitorMatrix = v === true || v === 'true' || v === 1 || v === '1';
  }
  const title = String(o.competitor_matrix_title ?? o.competitorMatrixTitle ?? '').trim();
  if (title) parsed.competitorMatrixTitle = title;
  const intro = String(o.competitor_matrix_intro ?? o.competitorMatrixIntro ?? '').trim();
  if (intro) parsed.competitorMatrixIntro = intro;
  if (competitorRows) parsed.competitorRows = competitorRows;
  const reviewUrl = String(o.google_review_url ?? o.googleReviewUrl ?? '').trim();
  if (reviewUrl) parsed.googleReviewUrl = reviewUrl;
  if (Number.isFinite(delayRaw) && delayRaw >= 0) {
    parsed.reviewRequestDelayMinutes = Math.min(24 * 60, Math.round(delayRaw));
  }
  if (o.review_request_whatsapp_enabled !== undefined || o.reviewRequestWhatsAppEnabled !== undefined) {
    const v = o.review_request_whatsapp_enabled ?? o.reviewRequestWhatsAppEnabled;
    parsed.reviewRequestWhatsAppEnabled = v === true || v === 'true' || v === 1 || v === '1';
  }
  const primary = String(o.gbp_primary_category ?? o.gbpPrimaryCategory ?? '').trim();
  if (primary) parsed.gbpPrimaryCategory = primary;
  if (gbpSecondaryCategories) parsed.gbpSecondaryCategories = gbpSecondaryCategories;
  if (gbpServiceAreas) parsed.gbpServiceAreas = gbpServiceAreas;
  if (gbpAttributes) parsed.gbpAttributes = gbpAttributes;
  const parentMarket = String(o.parent_market_name ?? o.parentMarketName ?? '').trim();
  if (parentMarket) parsed.parentMarketName = parentMarket;
  if (o.emit_service_offers !== undefined || o.emitServiceOffers !== undefined) {
    const v = o.emit_service_offers ?? o.emitServiceOffers;
    parsed.emitServiceOffers = v === true || v === 'true' || v === 1 || v === '1';
  }
  const offerUnit = String(o.offer_unit_text ?? o.offerUnitText ?? '').trim();
  if (offerUnit) parsed.offerUnitText = offerUnit;
  if (o.emit_blog_posting !== undefined || o.emitBlogPosting !== undefined) {
    const v = o.emit_blog_posting ?? o.emitBlogPosting;
    parsed.emitBlogPosting = v === true || v === 'true' || v === 1 || v === '1';
  }
  const author = String(o.blog_author_name ?? o.blogAuthorName ?? '').trim();
  if (author) parsed.blogAuthorName = author;
  if (o.emit_sitelink_navigation !== undefined || o.emitSitelinkNavigation !== undefined) {
    const v = o.emit_sitelink_navigation ?? o.emitSitelinkNavigation;
    parsed.emitSitelinkNavigation = v === true || v === 'true' || v === 1 || v === '1';
  }
  const sitelinks = parseSitelinkItems(o.sitelink_items ?? o.sitelinkItems);
  if (sitelinks) parsed.sitelinkItems = sitelinks;
  if (o.emit_website_search !== undefined || o.emitWebsiteSearch !== undefined) {
    const v = o.emit_website_search ?? o.emitWebsiteSearch;
    parsed.emitWebsiteSearch = v === true || v === 'true' || v === 1 || v === '1';
  }
  if (o.emit_image_objects !== undefined || o.emitImageObjects !== undefined) {
    const v = o.emit_image_objects ?? o.emitImageObjects;
    parsed.emitImageObjects = v === true || v === 'true' || v === 1 || v === '1';
  }
  const videoEmbedUrl = String(o.video_embed_url ?? o.videoEmbedUrl ?? '').trim();
  if (videoEmbedUrl) parsed.videoEmbedUrl = videoEmbedUrl;
  const videoName = String(o.video_name ?? o.videoName ?? '').trim();
  if (videoName) parsed.videoName = videoName;
  const videoThumbnailUrl = String(o.video_thumbnail_url ?? o.videoThumbnailUrl ?? '').trim();
  if (videoThumbnailUrl) parsed.videoThumbnailUrl = videoThumbnailUrl;
  const videoUploadDate = String(o.video_upload_date ?? o.videoUploadDate ?? '').trim();
  if (videoUploadDate) parsed.videoUploadDate = videoUploadDate;

  return Object.keys(parsed).length > 0 ? parsed : undefined;
}

export function serializeLocalSeoCampaign(config: LocalSeoCampaignConfig): Record<string, unknown> {
  return {
    show_hq_map_on_location_pages: config.showHqMapOnLocationPages,
    show_competitor_matrix: config.showCompetitorMatrix,
    competitor_matrix_title: config.competitorMatrixTitle,
    competitor_matrix_intro: config.competitorMatrixIntro,
    competitor_rows: config.competitorRows.map((row) => ({
      name: row.name,
      positioning: row.positioning,
      advantage: row.advantage,
    })),
    google_review_url: config.googleReviewUrl,
    review_request_delay_minutes: config.reviewRequestDelayMinutes,
    review_request_whatsapp_enabled: config.reviewRequestWhatsAppEnabled,
    gbp_primary_category: config.gbpPrimaryCategory,
    gbp_secondary_categories: config.gbpSecondaryCategories,
    gbp_service_areas: config.gbpServiceAreas,
    gbp_attributes: config.gbpAttributes,
    parent_market_name: config.parentMarketName,
    emit_service_offers: config.emitServiceOffers,
    offer_unit_text: config.offerUnitText,
    emit_blog_posting: config.emitBlogPosting,
    blog_author_name: config.blogAuthorName,
    emit_sitelink_navigation: config.emitSitelinkNavigation,
    sitelink_items: config.sitelinkItems,
    emit_website_search: config.emitWebsiteSearch,
    emit_image_objects: config.emitImageObjects,
    video_embed_url: config.videoEmbedUrl,
    video_name: config.videoName,
    video_thumbnail_url: config.videoThumbnailUrl,
    video_upload_date: config.videoUploadDate,
  };
}

export function resolveLocalSeoCampaign(
  org?: Partial<LocalSeoCampaignConfig> | null,
  platform?: Partial<LocalSeoCampaignConfig> | null,
  isSaas = false,
): LocalSeoCampaignConfig {
  const defaults = getCodeDefaultLocalSeoCampaign(isSaas);
  const plat = platform ?? {};
  const o = org ?? {};
  return {
    showHqMapOnLocationPages:
      o.showHqMapOnLocationPages ?? plat.showHqMapOnLocationPages ?? defaults.showHqMapOnLocationPages,
    showCompetitorMatrix:
      o.showCompetitorMatrix ?? plat.showCompetitorMatrix ?? defaults.showCompetitorMatrix,
    competitorMatrixTitle:
      o.competitorMatrixTitle?.trim() || plat.competitorMatrixTitle?.trim() || defaults.competitorMatrixTitle,
    competitorMatrixIntro:
      o.competitorMatrixIntro?.trim() || plat.competitorMatrixIntro?.trim() || defaults.competitorMatrixIntro,
    competitorRows:
      o.competitorRows && o.competitorRows.length > 0
        ? o.competitorRows
        : plat.competitorRows && plat.competitorRows.length > 0
          ? plat.competitorRows
          : defaults.competitorRows,
    googleReviewUrl: o.googleReviewUrl?.trim() || plat.googleReviewUrl?.trim() || defaults.googleReviewUrl,
    reviewRequestDelayMinutes:
      o.reviewRequestDelayMinutes ?? plat.reviewRequestDelayMinutes ?? defaults.reviewRequestDelayMinutes,
    reviewRequestWhatsAppEnabled:
      o.reviewRequestWhatsAppEnabled ??
      plat.reviewRequestWhatsAppEnabled ??
      defaults.reviewRequestWhatsAppEnabled,
    gbpPrimaryCategory:
      o.gbpPrimaryCategory?.trim() || plat.gbpPrimaryCategory?.trim() || defaults.gbpPrimaryCategory,
    gbpSecondaryCategories:
      o.gbpSecondaryCategories && o.gbpSecondaryCategories.length > 0
        ? o.gbpSecondaryCategories
        : plat.gbpSecondaryCategories && plat.gbpSecondaryCategories.length > 0
          ? plat.gbpSecondaryCategories
          : defaults.gbpSecondaryCategories,
    gbpServiceAreas:
      o.gbpServiceAreas && o.gbpServiceAreas.length > 0
        ? o.gbpServiceAreas
        : plat.gbpServiceAreas && plat.gbpServiceAreas.length > 0
          ? plat.gbpServiceAreas
          : defaults.gbpServiceAreas,
    gbpAttributes:
      o.gbpAttributes && o.gbpAttributes.length > 0
        ? o.gbpAttributes
        : plat.gbpAttributes && plat.gbpAttributes.length > 0
          ? plat.gbpAttributes
          : defaults.gbpAttributes,
    parentMarketName:
      o.parentMarketName?.trim() || plat.parentMarketName?.trim() || defaults.parentMarketName,
    emitServiceOffers: o.emitServiceOffers ?? plat.emitServiceOffers ?? defaults.emitServiceOffers,
    offerUnitText: o.offerUnitText?.trim() || plat.offerUnitText?.trim() || defaults.offerUnitText,
    emitBlogPosting: o.emitBlogPosting ?? plat.emitBlogPosting ?? defaults.emitBlogPosting,
    blogAuthorName: o.blogAuthorName?.trim() || plat.blogAuthorName?.trim() || defaults.blogAuthorName,
    emitSitelinkNavigation:
      o.emitSitelinkNavigation ?? plat.emitSitelinkNavigation ?? defaults.emitSitelinkNavigation,
    sitelinkItems:
      o.sitelinkItems && o.sitelinkItems.length > 0
        ? o.sitelinkItems
        : plat.sitelinkItems && plat.sitelinkItems.length > 0
          ? plat.sitelinkItems
          : defaults.sitelinkItems,
    emitWebsiteSearch: o.emitWebsiteSearch ?? plat.emitWebsiteSearch ?? defaults.emitWebsiteSearch,
    emitImageObjects: o.emitImageObjects ?? plat.emitImageObjects ?? defaults.emitImageObjects,
    videoEmbedUrl: o.videoEmbedUrl?.trim() || plat.videoEmbedUrl?.trim() || defaults.videoEmbedUrl,
    videoName: o.videoName?.trim() || plat.videoName?.trim() || defaults.videoName,
    videoThumbnailUrl:
      o.videoThumbnailUrl?.trim() || plat.videoThumbnailUrl?.trim() || defaults.videoThumbnailUrl,
    videoUploadDate: o.videoUploadDate?.trim() || plat.videoUploadDate?.trim() || defaults.videoUploadDate,
  };
}
